'use client'

import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FaMagic, FaGem, FaTimes, FaVideo } from 'react-icons/fa'
import { BsFillChatLeftDotsFill } from 'react-icons/bs'
import ClipLoader from 'react-spinners/ClipLoader'

// Textarea grows with content up to this height, then scrolls.
const MAX_TEXTAREA_HEIGHT = 120

export default function PromptBar({
  prompt,
  setPrompt,
  onGenerate,
  isGenerating,
  gemCost = 0,
  charImage,
  onClearChar,
  containerRef,
  // 'image' drives image generation; 'video' drives text-to-video (Video tab).
  mode = 'image',
}) {
  const { t } = useTranslation()
  const isVideo = mode === 'video'
  // Set when the user tries to generate with an empty prompt, so we can surface an
  // inline hint instead of silently doing nothing. Cleared as soon as they type.
  const [emptyError, setEmptyError] = useState(false)
  const submit = (e) => {
    e.preventDefault()
    if (isGenerating) return
    // Never fire a generation (and burn a gem) on an empty/whitespace prompt — tell the
    // user why instead of no-oping.
    if (!prompt?.trim()) {
      setEmptyError(true)
      textareaRef.current?.focus()
      return
    }
    onGenerate?.()
  }

  // Re-runs on every prompt change, including external clears/loads.
  const textareaRef = useRef(null)
  useEffect(() => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, MAX_TEXTAREA_HEIGHT)}px`
  }, [prompt])

  // Make the whole card one big target: clicking anywhere non-interactive — the
  // header/label row, the padding, the empty space below a short prompt — focuses
  // the prompt input (caret to end). The textarea itself and the controls
  // (send button, char remove) are skipped so in-text clicks place the caret
  // precisely and buttons still fire. mousedown + preventDefault avoids the focus
  // flicker/deselection a plain click would cause.
  const focusPrompt = (e) => {
    if (e.target.closest('button, a, textarea')) return
    e.preventDefault()
    const el = textareaRef.current
    if (!el) return
    el.focus()
    const end = el.value.length
    el.setSelectionRange(end, end)
  }

  return (
    <div
      ref={containerRef}
      // `bottom` lifts above the cookie-consent banner when it's showing; the var
      // resolves to 0px otherwise (see ConsentBanner), so no banner = flush bottom.
      //
      // The keyboard lift is a `transform`, deliberately, for two reasons: it's
      // outside `transition-[bottom]`, so the bar tracks the keyboard frame by
      // frame instead of easing 200ms behind it; and it's compositor-only, so
      // riding the keyboard animation costs no layout. The bar overlays the feed
      // rather than shortening it — the feed is snap-scrolling, and reflowing it
      // mid-typing would shift the card you're prompting about.
      style={{
        bottom: 'var(--consent-banner-height, 0px)',
        transform: 'translateY(calc(var(--pc-keyboard-inset, 0px) * -1))',
      }}
      className="pointer-events-none absolute inset-x-0 z-30 px-3 pb-4 transition-[bottom] duration-200 md:px-6 md:pb-6"
    >
      <form
        onSubmit={submit}
        onMouseDown={focusPrompt}
        // focus-within lights up the whole card when the (transparent) textarea
        // inside is focused — same white border + ring the negative-prompt box
        // shows on focus. On the empty-prompt error the border stays red with a
        // red-tinted ring so the warning isn't masked.
        className={`pointer-events-auto relative mx-auto w-full max-w-[680px] min-h-[92px] rounded-[22px] border bg-[var(--middle-bg-color)] px-4 py-3 shadow-2xl transition ${
          emptyError
            ? 'border-red-500 focus-within:ring-2 focus-within:ring-red-500/30'
            : 'border-[var(--foreground-bg-color)] focus-within:border-white focus-within:ring-2 focus-within:ring-white/15'
        }`}
      >
        {/* Cost chip, top-right. The right column (this + the send button below) is the
            only reserved space; text is kept out of it via the textarea's pr. */}
        <div className="absolute top-2.5 right-2.5 inline-flex items-center h-5 px-2 rounded-full bg-[var(--foreground-bg-color)] text-white text-[10px]">
          <FaGem className="mr-0.5 text-[var(--main-bg-color)]" size={9} />
          <span className="font-semibold">{gemCost}</span>
        </div>

        {/* Small header so the box is unmistakably the prompt input. pr-14 clears the chip.
            Doubles as the empty-prompt hint (turns red with a message). The selected
            character's avatar sits inline here (image + remove only) so it doesn't eat a
            whole row of prompt space. */}
        <div
          className={`mb-1.5 flex items-center gap-1.5 pr-14 min-h-[20px] text-xs font-semibold transition-colors ${
            emptyError ? 'text-red-400' : 'text-white'
          }`}
        >
          <span className="inline-flex items-center gap-1.5">
            <BsFillChatLeftDotsFill size={12} />
            {emptyError ? t('enterPromptFirst') : t('prompt')}
          </span>

          {charImage ? (
            // The whole chip is the remove control (not just the ✕) so it's an
            // easy target. Kept short (h-5) and the row above reserves min-h, so
            // adding/removing a character never resizes the prompt box.
            <button
              type="button"
              onClick={onClearChar}
              title={t('removeCharacter')}
              aria-label={t('removeCharacter')}
              className="group inline-flex items-center gap-1 h-5 pl-0.5 pr-1.5 rounded-full bg-[var(--foreground-bg-color)] text-white"
            >
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={charImage}
                alt={t('characterLabel')}
                className="h-4 w-4 rounded-full object-cover"
              />
              <FaTimes
                size={9}
                className="text-white/60 group-hover:text-white"
              />
            </button>
          ) : null}
        </div>

        {/* Full-width textarea (max horizontal space); grows tall (max vertical space).
            pr-14 keeps text clear of the top-right chip and bottom-right send button.
            min-h fills the box down to the send-button row at rest so the whole area
            is a clickable target (not just the single text line); the textarea still
            grows with content up to MAX_TEXTAREA_HEIGHT — min-height is only a floor. */}
        <textarea
          ref={textareaRef}
          rows={1}
          value={prompt}
          aria-invalid={emptyError}
          onChange={(e) => {
            setPrompt(e.target.value)
            if (emptyError && e.target.value.trim()) setEmptyError(false)
          }}
          placeholder={
            isVideo ? t('describeYourVideo') : t('describeYourImage')
          }
          style={{ maxHeight: MAX_TEXTAREA_HEIGHT }}
          className="block w-full min-h-[46px] resize-none overflow-y-auto bg-transparent border-0 p-0 pr-14 text-white text-base leading-snug focus:outline-none focus:ring-0 placeholder:text-gray-500"
        />

        <button
          type="submit"
          disabled={isGenerating}
          aria-label={t('generate')}
          className="absolute bottom-2.5 right-2.5 h-11 w-11 flex items-center justify-center rounded-full bg-[var(--main-bg-color)] text-white transition-transform hover:scale-105 active:scale-95 disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
        >
          {isGenerating ? (
            <ClipLoader color="#FFF" size={16} />
          ) : isVideo ? (
            <FaVideo size={16} />
          ) : (
            <FaMagic size={16} />
          )}
        </button>
      </form>
    </div>
  )
}
